import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, Menu, X, Truck, LogIn, LogOut, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PHONE_DISPLAY, PHONE_TEL, WHATSAPP_URL } from "@/constants/site";
import { useAuth } from "@/context/AuthContext";

const links = [
  { href: "#servizi", label: "Servizi" },
  { href: "#come-funziona", label: "Come funziona" },
  { href: "#zona", label: "Zona servita" },
  { href: "#preventivo", label: "Preventivo" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const { user, login, logout } = useAuth();
  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 border-b border-slate-200 bg-white/80 backdrop-blur-xl"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        <a href="#top" data-testid="logo-link" className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-orange-500 text-white">
            <Truck className="h-5 w-5" />
          </span>
          <span className="font-display text-xl font-semibold tracking-tight text-slate-900">
            Sgomberiamo
          </span>
        </a>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="desktop-nav">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.href.slice(1)}`}
              className="text-sm font-medium text-slate-600 transition-colors duration-200 hover:text-orange-600"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={PHONE_TEL}
            data-testid="header-call-button"
            className="flex items-center gap-2 text-sm font-semibold text-slate-900 transition-colors duration-200 hover:text-orange-600"
          >
            <Phone className="h-4 w-4 text-orange-500" />
            {PHONE_DISPLAY}
          </a>
          {user ? (
            <>
              {user.is_admin && (
                <Link
                  to="/admin"
                  data-testid="header-admin-link"
                  className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 transition-colors duration-200 hover:text-orange-600"
                >
                  <ShieldCheck className="h-4 w-4" /> Area riservata
                </Link>
              )}
              <span className="max-w-[140px] truncate text-sm text-slate-500" data-testid="header-user-name">{user.name || user.email}</span>
              <button
                onClick={logout}
                data-testid="header-logout-button"
                className="flex items-center gap-1 text-sm font-medium text-slate-500 transition-colors duration-200 hover:text-orange-600"
              >
                <LogOut className="h-4 w-4" /> Esci
              </button>
            </>
          ) : (
            <button
              onClick={login}
              data-testid="header-login-button"
              className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 transition-colors duration-200 hover:text-orange-600"
            >
              <LogIn className="h-4 w-4" /> Accedi
            </button>
          )}
          <Button
            asChild
            data-testid="header-quote-button"
            className="rounded-md bg-orange-500 font-semibold text-white transition-transform duration-200 hover:scale-[1.03] hover:bg-orange-600"
          >
            <a href="#preventivo">Preventivo gratuito</a>
          </Button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-slate-200 bg-white px-6 py-6 lg:hidden" data-testid="mobile-menu">
          <nav className="flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`mobile-nav-${l.href.slice(1)}`}
                onClick={() => setOpen(false)}
                className="text-base font-medium text-slate-700"
              >
                {l.label}
              </a>
            ))}
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-whatsapp-button"
              className="flex items-center gap-2 text-base font-semibold text-green-600"
            >
              <MessageCircle className="h-5 w-5" /> WhatsApp
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
